import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Linking,
  Platform,
} from "react-native";
import { format } from "date-fns";
import { useAuth } from "../../contexts/AuthContext";
import {
  getCrawledOpportunities,
  approveCrawledOpportunity,
  rejectCrawledOpportunity,
} from "../../services/parseService";
import CrawledOpportunityEditor from "./CrawledOpportunityEditor";

interface Props {
  onReloadCrawledCount: () => void;
}

const CrawledOpportunitiesScreen: React.FC<Props> = ({ onReloadCrawledCount }) => {
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setIsLoading(true);
      const res = await getCrawledOpportunities("Pending");
      setItems(res);
      onReloadCrawledCount();
    } catch (err) {
      setError("Failed to load crawled opportunities.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (id: string) => {
    try {
      setProcessingId(id);
      await approveCrawledOpportunity(id, currentUser?.id);
      await loadItems();
    } catch (err) {
      Alert.alert("Error", "Failed to approve. It may already exist or be missing fields.");
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setProcessingId(id);
      await rejectCrawledOpportunity(id, currentUser?.id);
      await loadItems();
    } catch (err) {
      Alert.alert("Error", "Failed to reject.");
    } finally {
      setProcessingId(null);
    }
  };

  const openLink = (url?: string) => {
    if (!url) return;
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else {
      Linking.openURL(url);
    }
  };

  const renderItem = ({ item }: { item: any }) => {
    const get = (key: string) => (item.get ? item.get(key) : item[key]);
    const typeVal = get("type");
    const typeStr = Array.isArray(typeVal) ? typeVal.join(", ") : typeVal;
    const confidence = get("confidence");
    const dedupScore = get("dedupScore");
    const sourceUrl = get("sourceUrl") || get("website");
    const website = get("website");
    const linkNeedsReview = !!get("linkNeedsReview");
    const isProcessing = processingId === (item.id || item.objectId);
    const closeDate = get("applicationCloseDate");
    const dupHigh = typeof dedupScore === "number" && dedupScore >= 0.7;

    return (
      <View style={styles.card}>
        <View style={styles.header}>
          <Text style={styles.title} numberOfLines={2}>
            {get("title")}
          </Text>
          <Text style={styles.date}>
            {(() => {
              try {
                const raw = get("crawledAt") || get("createdAt");
                const date = raw instanceof Date ? raw : raw ? new Date(raw) : null;
                return date && !isNaN(date.getTime()) ? format(date, "MMM d, yyyy") : "";
              } catch {
                return "";
              }
            })()}
          </Text>
        </View>

        <View style={styles.metaRow}>
          {!!get("organization") && <Text style={styles.org}>{get("organization")}</Text>}
          {!!get("location") && <Text style={styles.meta}>· {get("location")}</Text>}
          {!!typeStr && <Text style={styles.meta}>· {typeStr}</Text>}
        </View>

        <Text style={styles.description} numberOfLines={4}>
          {get("description")}
        </Text>

        {!!get("eligibility") && (
          <Text style={styles.field}>
            <Text style={styles.fieldLabel}>Eligibility: </Text>
            {get("eligibility")}
          </Text>
        )}
        {!!get("cost") && (
          <Text style={styles.field}>
            <Text style={styles.fieldLabel}>Cost: </Text>
            {get("cost")}
          </Text>
        )}
        {!!closeDate && (
          <Text style={styles.field}>
            <Text style={styles.fieldLabel}>Closes: </Text>
            {closeDate}
          </Text>
        )}

        <View style={styles.badges}>
          {typeof confidence === "number" && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>confidence {(confidence * 100).toFixed(0)}%</Text>
            </View>
          )}
          {typeof dedupScore === "number" && (
            <View style={[styles.badge, dupHigh && styles.badgeWarn]}>
              <Text style={[styles.badgeText, dupHigh && styles.badgeWarnText]}>
                similarity {(dedupScore * 100).toFixed(0)}%{dupHigh ? " ⚠ possible dup" : ""}
              </Text>
            </View>
          )}
          {linkNeedsReview && (
            <View style={[styles.badge, styles.badgeWarn]}>
              <Text style={[styles.badgeText, styles.badgeWarnText]}>⚠ link needs review</Text>
            </View>
          )}
        </View>

        {/* Kept distinct on purpose: the page the crawler READ is often a roundup, and is not the
            opportunity's own site. Showing only one URL invites publishing the wrong one. */}
        {!!website && (
          <TouchableOpacity onPress={() => openLink(website)}>
            <Text style={styles.link} numberOfLines={1}>
              <Text style={styles.linkLabel}>Opportunity link: </Text>
              {website}
            </Text>
          </TouchableOpacity>
        )}
        {!!sourceUrl && sourceUrl !== website && (
          <TouchableOpacity onPress={() => openLink(sourceUrl)}>
            <Text style={[styles.link, styles.linkMuted]} numberOfLines={1}>
              <Text style={styles.linkLabel}>Found on: </Text>
              {sourceUrl}
            </Text>
          </TouchableOpacity>
        )}

        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.button, styles.review, isProcessing && styles.disabled]}
            onPress={() => setEditingItem(item)}
            disabled={isProcessing}
          >
            <Text style={styles.reviewButtonText}>Review &amp; Edit</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.approve, isProcessing && styles.disabled]}
            onPress={() => {
              // A flagged item with no link would publish with a blank website — make the admin
              // set the real URL in the editor first instead of one-click publishing.
              if (linkNeedsReview && !website) {
                setEditingItem(item);
                return;
              }
              handleApprove(item.id || item.objectId);
            }}
            disabled={isProcessing}
          >
            <Text style={styles.buttonText}>{isProcessing ? "Working…" : "Approve & Publish"}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.reject, isProcessing && styles.disabled]}
            onPress={() => handleReject(item.id || item.objectId)}
            disabled={isProcessing}
          >
            <Text style={styles.buttonText}>Reject</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#3182CE" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center}>
        <Text>{error}</Text>
      </View>
    );
  }

  if (items.length === 0) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyText}>No crawled opportunities pending review.</Text>
      </View>
    );
  }

  return (
    <>
      <FlatList
        data={items}
        keyExtractor={(item) => item.id || item.objectId}
        renderItem={renderItem}
        contentContainerStyle={{ padding: 16 }}
      />
      {editingItem && (
        <CrawledOpportunityEditor
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onProcessed={async () => {
            setEditingItem(null);
            await loadItems();
          }}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    fontFamily: "Inter-SemiBold",
    flex: 1,
    marginRight: 8,
  },
  date: {
    fontSize: 13,
    color: "#718096",
    fontFamily: "Inter-Regular",
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    marginBottom: 8,
  },
  org: {
    fontSize: 13,
    color: "#2563EB",
    fontFamily: "Inter-Medium",
  },
  meta: {
    fontSize: 13,
    color: "#718096",
    marginLeft: 4,
    fontFamily: "Inter-Regular",
  },
  description: {
    fontSize: 14,
    color: "#4A5568",
    marginBottom: 8,
    fontFamily: "Inter-Regular",
  },
  field: {
    fontSize: 13,
    color: "#4A5568",
    marginBottom: 4,
    fontFamily: "Inter-Regular",
  },
  fieldLabel: {
    fontWeight: "600",
    color: "#2D3748",
    fontFamily: "Inter-SemiBold",
  },
  badges: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
    marginBottom: 8,
  },
  badge: {
    backgroundColor: "#EEF2FF",
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
  },
  badgeText: {
    fontSize: 12,
    color: "#3730A3",
    fontFamily: "Inter-Medium",
  },
  badgeWarn: {
    backgroundColor: "#FEF2F2",
  },
  badgeWarnText: {
    color: "#DC2626",
  },
  link: {
    fontSize: 13,
    color: "#3182CE",
    marginBottom: 8,
    fontFamily: "Inter-Regular",
  },
  linkMuted: {
    color: "#718096",
  },
  linkLabel: {
    color: "#718096",
    fontFamily: "Inter-Medium",
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  button: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 6,
  },
  buttonText: {
    color: "#fff",
    fontFamily: "Inter-SemiBold",
  },
  approve: { backgroundColor: "#10B981" },
  reject: { backgroundColor: "#E53E3E" },
  review: {
    backgroundColor: "#EDE9FE",
    borderWidth: 1,
    borderColor: "#DDD6FE",
  },
  reviewButtonText: {
    color: "#5B21B6",
    fontFamily: "Inter-SemiBold",
  },
  disabled: { opacity: 0.6 },
  center: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  emptyText: {
    fontSize: 15,
    color: "#718096",
    fontFamily: "Inter-Medium",
  },
});

export default CrawledOpportunitiesScreen;
